import { useQuery } from "@apollo/client";
import { useSplitTreatments } from "@splitsoftware/splitio-react";
import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import haversine from "haversine-distance";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import LoadingAnimation from "@/components/loading-animation";
import { jsxToNode } from "@/components/map-pin";
import CategoryPin from "@/components/map-pin/category-pin";
import ClusterPin from "@/components/map-pin/cluster-pin";
import { HalloweenActiveIcon, HalloweenIcon } from "@/components/map-pin/icons";
import MapKitScript from "@/components/mapkit-script";
import { Button } from "@/components/ui/button";
import { MOBILE_MAP_HEIGHT, MOBILE_MAP_HEIGHT_IOS } from "@/helpers/constants";
import { regionByCoords } from "@/helpers/location";
import useAuth from "@/hooks/use-auth";
import { useMapKitLoad } from "@/hooks/use-map-kit-load";
import type { ExploreSearchParams } from "@/routes/explore/index";

import {
  IncludeEnum,
  type MapDataType,
  type MapLocationType,
  type VariablesType,
} from "../types";
import { QUERY } from "./graphql";
import MapOverlay from "./map-overlay";
import MarkerLocations from "./marker-locations";

const MAP_STATE_STORAGE_KEY = "morty_map_state";

interface MapState {
  center: [number, number];
  url: string;
  zoomLevel: number;
}

interface GeoJSONPoint extends Array<number | number> {
  0: number;
  1: number;
}

const IGNORE_BOUNDS_CHANGE_TIMEOUT = 2000;

interface MapKitProps {
  hasListItems: boolean;
  hoveredObject: string | null;
  isIos: boolean;
  variables: VariablesType;
}

export default function MapKit({
  hasListItems,
  hoveredObject,
  isIos,
  variables,
}: MapKitProps) {
  const { isMapInitialized, mapRef, mapContainerRef, handleMapKitLoad } =
    useMapKitLoad({ wheelZoom: true });
  const mapRefreshed = useRef(new Date());
  const [searchButton, setSearchButton] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState<MapLocationType | null>(
    null
  );
  const [hasRestoredState, setHasRestoredState] = useState(false);
  const objectIds = useRef<string[]>([]);

  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const navigate = useNavigate();
  const asPath = useRouterState({ select: (s) => s.location.href });
  const { queryContext } = useAuth();
  const { data, loading } = useQuery<MapDataType, VariablesType>(QUERY, {
    ...queryContext({
      ...variables,
      groupByLocation: true,
      pageSize:
        search.include &&
        (search.include === IncludeEnum.PLAYED ||
          search.include === IncludeEnum.SAVED)
          ? 300
          : 200,
    }),
  });
  const { treatments } = useSplitTreatments({
    names: ["xmas_bash_2023", "halloween_party"],
  });
  const objects: MapLocationType[] = useMemo(
    () => (data ? data.games.objects : []),
    [data]
  );

  const saveMapState = useCallback(() => {
    if (!mapRef.current) {
      return;
    }

    const state: MapState = {
      center: [mapRef.current.center.latitude, mapRef.current.center.longitude],
      url: asPath,
      zoomLevel: mapRef.current.cameraDistance,
    };

    sessionStorage.setItem(MAP_STATE_STORAGE_KEY, JSON.stringify(state));
  }, [asPath, mapRef]);

  const resetSearchButton = () => {
    setSearchButton(false);
    mapRefreshed.current = new Date();
  };

  const getObjCoords = (obj: MapLocationType) =>
    obj.location
      ? [obj.location.coordinates[1], obj.location.coordinates[0]]
      : null;

  const createMarker = (location: MapLocationType) => {
    const position = getObjCoords(location);

    if (!position) {
      return null;
    }

    // biome-ignore lint/correctness/noUndeclaredVariables: legacy
    const center = new mapkit.Coordinate(position[0], position[1]);
    const commonProps = {
      clusteringIdentifier: "location",
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      collisionMode: mapkit.Annotation.CollisionMode.Rectangle,
      data: {
        isActive: location === selectedMarker || location.id === hoveredObject,
        location,
      },
    };
    const isXmas =
      treatments.xmas_bash_2023?.treatment === "on" &&
      location.games.some((game) =>
        game.tags.some((tag) => tag.id === "christmas")
      );
    const isHalloween =
      treatments.halloween_party?.treatment === "on" &&
      location.games.some(
        (game) =>
          game.isScary ||
          game.tags.some((tag) =>
            ["spooky", "scary", "very-scary"].includes(tag.id)
          )
      );

    let marker: mapkit.Annotation;
    if (isXmas) {
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      marker = new mapkit.ImageAnnotation(center, {
        ...commonProps,
        url: ((url) => ({
          1: `${url}.png`,
          2: `${url}@2x.png`,
          3: `${url}@3x.png`,
        }))(
          `/images/map/snowman-${commonProps.data.isActive ? "active-" : ""}marker`
        ),
      });
    } else {
      let icon: typeof HalloweenActiveIcon | undefined;
      if (isHalloween) {
        icon = commonProps.data.isActive ? HalloweenActiveIcon : HalloweenIcon;
      }

      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      marker = new mapkit.Annotation(
        center,
        (_coordinate, options) =>
          jsxToNode(
            <CategoryPin
              icon={icon}
              isActive={options.data.isActive}
              location={options.data.location}
            />
          ),
        {
          ...commonProps,
          size: { height: 83, width: 77 },
        }
      );
    }
    marker.addEventListener("select", () => {
      resetSearchButton();
      if (!hasRestoredState) {
        mapRef.current?.setCenterAnimated(center, true);
      }
      setSelectedMarker(location);
    });

    return marker;
  };

  const handleSearchButton = async () => {
    if (!mapRef.current) {
      return;
    }

    setSearchButton(false);

    const { latitude, longitude } = mapRef.current.center;
    const { eastLongitude, northLatitude, southLatitude, westLongitude } =
      mapRef.current.region.toBoundingRegion();
    const center = `${latitude},${longitude}`;
    const region = await regionByCoords(center);

    navigate({
      search: {
        ...search,
        boundingBox: `${southLatitude},${westLongitude},${northLatitude},${eastLongitude}`,
        location: center,
      },
      to: `/explore${region ? `/${encodeURIComponent(region)}` : ""}`,
    });
  };

  const center = useMemo(() => {
    if (typeof search.location === "string") {
      const parsed = search.location
        .split(",")
        .map((c) => Number.parseFloat(c));
      if (
        parsed.length === 2 &&
        !Number.isNaN(parsed[0]) &&
        !Number.isNaN(parsed[1])
      ) {
        return parsed;
      }
    }
    return [34.052_234_2, -118.243_684_9];
  }, [search.location]);

  const addMarkers = () => {
    if (!mapRef.current) {
      return;
    }
    if (!objects.length) {
      return;
    }

    mapRef.current.removeAnnotations(mapRef.current.annotations);

    const markers = objects.reduce((accum, obj) => {
      const m = createMarker(obj);

      if (m) {
        accum.push(m);
      }

      return accum;
    }, [] as mapkit.Annotation[]);

    mapRef.current.addAnnotations(markers);
    resetSearchButton();
  };

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy
  const centerOnResults = () => {
    if (!(mapRef.current && objects.length)) {
      return;
    }

    // Get all location coordinates from map items
    const locations: [number, number][] = objects
      .map((obj) => getObjCoords(obj))
      .filter((coords): coords is [number, number] => coords !== null);

    const boundsLocations: [number, number][] = [];

    // If we have locations and a search center, and we're not using a custom bounding box
    if (locations.length > 0 && center && !search.boundingBox) {
      let distance = 50.0;

      // Keep expanding the search radius until we have at least 5 locations or reach max distance
      while (boundsLocations.length < 5 && distance <= 1000) {
        boundsLocations.length = 0;
        for (const location of locations) {
          if (
            haversine(location as GeoJSONPoint, center as GeoJSONPoint) <=
            distance * 1609.34
          ) {
            boundsLocations.push(location);
          }
        }
        distance += 50;
      }
    }

    // Choose which locations to use for bounds calculation
    const locationsForBounds =
      boundsLocations.length > 0 ? boundsLocations : locations;

    // Handle single location case - zoom in closer
    if (objects.length === 1 && locationsForBounds.length === 1) {
      const [lat, lng] = locationsForBounds[0];
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      const coordinate = new mapkit.Coordinate(lat, lng);
      mapRef.current.setCenterAnimated(coordinate, true);
    } else if (locationsForBounds.length > 0) {
      let minLat = Number.POSITIVE_INFINITY;
      let maxLat = Number.NEGATIVE_INFINITY;
      let minLng = Number.POSITIVE_INFINITY;
      let maxLng = Number.NEGATIVE_INFINITY;

      for (const [lat, lng] of locationsForBounds) {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
      }

      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;
      const latSpan = Math.max((maxLat - minLat) * 1.3, 0.02);
      const lngSpan = Math.max((maxLng - minLng) * 1.3, 0.02);

      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      const region = new mapkit.CoordinateRegion(
        // biome-ignore lint/correctness/noUndeclaredVariables: legacy
        new mapkit.Coordinate(centerLat, centerLng),
        // biome-ignore lint/correctness/noUndeclaredVariables: legacy
        new mapkit.CoordinateSpan(latSpan, lngSpan)
      );
      mapRef.current.setRegionAnimated(region, true);
    }

    resetSearchButton();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need to re-render
  useEffect(() => {
    if (!(mapRef.current && objects.length)) {
      return;
    }

    // Check if objects have actually changed by comparing IDs
    const objectIdsSet = new Set(objectIds.current);
    let objectsIdsMatch = false;
    if (objects.length === objectIds.current.length) {
      objectsIdsMatch = true;
      for (const obj of objects) {
        if (!objectIdsSet.has(obj.id)) {
          objectsIdsMatch = false;
          break;
        }
      }
    }

    // Skip centering if objects haven't changed (prevents Apollo cache
    // re-emissions from triggering centerOnResults after pin clicks)
    if (objectsIdsMatch) {
      return;
    }

    centerOnResults();
  }, [objects, center, search.boundingBox]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: no worries
  useEffect(() => {
    addMarkers();
  }, [objects, treatments]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: you no need it
  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    const addMarkers: mapkit.Annotation[] = [];
    const removeMarkers: mapkit.Annotation[] = [];

    for (const a of mapRef.current.annotations) {
      const shouldBeActive =
        selectedMarker === a.data.location ||
        hoveredObject === a.data.location.id;
      if (a.data.isActive !== shouldBeActive) {
        removeMarkers.push(a);
        const marker = createMarker(a.data.location);
        if (marker) {
          addMarkers.push(marker);
        }
      }
    }

    mapRef.current.removeAnnotations(removeMarkers);
    mapRef.current.addAnnotations(addMarkers);
  }, [selectedMarker, hoveredObject]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dis good as well
  useEffect(() => {
    const objectIdsSet = new Set(objectIds.current);
    const objectsIdsMatch =
      objects.length === objectIds.current.length &&
      objects.every((obj) => objectIdsSet.has(obj.id));

    if (objectsIdsMatch) {
      return;
    }

    setSelectedMarker(null);
    resetSearchButton();
    objectIds.current = objects.map((obj) => obj.id);
  }, [objects]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: probably not needed
  useEffect(() => {
    if (!isMapInitialized) {
      return;
    }
    if (!mapContainerRef.current) {
      return;
    }
    if (mapRef.current) {
      return;
    }

    const savedState = sessionStorage.getItem(MAP_STATE_STORAGE_KEY);
    const state: MapState | null = savedState ? JSON.parse(savedState) : null;
    const shouldRestoreState = state && state.url === asPath;

    // biome-ignore lint/correctness/noUndeclaredVariables: legacy
    mapRef.current = new mapkit.Map(mapContainerRef.current, {
      annotationForCluster: (clusterAnnotation) => {
        // biome-ignore lint/correctness/noUndeclaredVariables: legacy
        const marker = new mapkit.Annotation(
          clusterAnnotation.coordinate,
          (_coordinate, options) =>
            jsxToNode(
              <ClusterPin
                count={options.data.count}
                isActive={options.data.isActive}
              />
            ),
          {
            data: {
              count: clusterAnnotation.memberAnnotations.length,
              isActive: clusterAnnotation.memberAnnotations.some(
                (a) => a.data.isActive
              ),
            },
            size: { height: 26, width: 26 },
          }
        );

        marker.addEventListener("select", () => {
          if (!hasRestoredState) {
            mapRef.current?.showItems(clusterAnnotation.memberAnnotations, {
              animate: true,
              // biome-ignore lint/correctness/noUndeclaredVariables: legacy
              padding: new mapkit.Padding(40, 40, 40, 40),
            });
          }
        });

        return marker;
      },
      center: shouldRestoreState
        ? // biome-ignore lint/correctness/noUndeclaredVariables: legacy
          new mapkit.Coordinate(state.center[0], state.center[1])
        : // biome-ignore lint/correctness/noUndeclaredVariables: legacy
          new mapkit.Coordinate(center[0], center[1]),
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      colorScheme: mapkit.Map.ColorSchemes.Dark,
      isRotationEnabled: false,
      isScrollEnabled: true,
      isZoomEnabled: true,
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      mapType: mapkit.Map.MapTypes.MutedStandard,
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      pointOfInterestFilter: mapkit.PointOfInterestFilter.excluding(
        Object.values(
          // biome-ignore lint/correctness/noUndeclaredVariables: legacy
          mapkit.PointOfInterestCategory
        ) as mapkit.PointOfInterestCategory[]
      ),
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      showsCompass: mapkit.FeatureVisibility.Hidden,
      showsMapTypeControl: false,
    });

    if (shouldRestoreState) {
      mapRef.current.setCameraDistanceAnimated(state.zoomLevel);
      setHasRestoredState(true);
    } else {
      // Use non-animated version so it doesn't conflict with centerOnResults animation
      mapRef.current.cameraDistance = 100_000;
    }

    mapRef.current.addEventListener("region-change-end", () => {
      if (
        Date.now() - mapRefreshed.current.getTime() >
        IGNORE_BOUNDS_CHANGE_TIMEOUT
      ) {
        setSearchButton(true);
        saveMapState();
      }
    });
    mapRef.current.addEventListener("scroll-start", () =>
      setSelectedMarker(null)
    );
    mapRef.current.addEventListener("single-tap", () =>
      setSelectedMarker(null)
    );

    resetSearchButton();
    addMarkers();

    // If search results are already loaded, center on them now.
    // The centering effect won't re-run since objects haven't changed
    // after this point, so we must center here.
    if (objects.length && !shouldRestoreState) {
      centerOnResults();
    }
  }, [isMapInitialized, mapContainerRef]);

  return (
    <div
      className="fixed left-0 h-(--map-height) w-full flex-col md:sticky md:top-20 md:right-0 md:left-auto md:z-4 md:flex md:h-[calc(100vh-80px)] md:w-1/2"
      style={
        {
          "--map-height": isIos ? MOBILE_MAP_HEIGHT_IOS : MOBILE_MAP_HEIGHT,
        } as React.CSSProperties
      }
    >
      <div className='relative h-full before:absolute before:inset-x-0 before:top-0 before:z-1 before:h-px before:bg-white/10 before:shadow-0-4-8 before:shadow-deep-cove/40 before:content-[""] before:md:opacity-0'>
        <div
          className="size-full [&_canvas]:contrast-140 [&_canvas]:grayscale"
          ref={mapContainerRef}
        />
        {searchButton && (
          <Button
            className="absolute top-5 left-1/2 h-7.5 -translate-x-1/2 rounded-[15px] bg-linear-to-b from-bright-pink to-dark-magenta px-4.5 font-medium text-13 shadow-0-0-10-5 shadow-dark-magenta/40"
            onClick={handleSearchButton}
            type="button"
          >
            Search in this area
          </Button>
        )}
        {selectedMarker && (
          <MarkerLocations key={selectedMarker.id} location={selectedMarker} />
        )}
      </div>
      {hasListItems && !loading && !objects.length && <MapOverlay />}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-light-black/60">
          <LoadingAnimation />
        </div>
      )}
      <MapKitScript onLoad={handleMapKitLoad} />
    </div>
  );
}
